import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import WrenchSvgIcon from './components/WrenchSvgIcon'
import NewDamageReportWizard from './components/NewDamageReport'

import { Snackbar } from '@mui/material'
import { Alert } from '@mui/material'

import { repairAndInquiriesApi, ServiceRequestItem } from '../../../api/repairAndInquiries'
import LoadingOverlay from '../../../components/LoadingOverlay'
import PageHeader from '../../../components/PageHeader'
import { NoRequests, Multipanel } from './components'
import { GlobalContext } from '../../../App'
import { ContextEntity } from '../../../customEntites/context.entity'

import './index.scss'
import { generateError } from '../../../services/error.service'

export const RepairAndInquiries = () => {
  const { t } = useTranslation()
  const [requests, setRequests] = useState<ServiceRequestItem[]>([])
  const [activeStatus, setActiveStatus] = useState(0)
  const [loading, setLoading] = useState(true)
  const { context }: ContextEntity = useContext(GlobalContext)
  const location = useLocation()
  const [showWizard, setShowWizard] = useState(false)
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [errorAlertMsg, setErrorAlertMsg] = useState('')
  const PAGE_SIZE = 10
  const [allRequests, setAllRequests] = useState<ServiceRequestItem[]>([])
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Fetch all tickets once (high pageSize) so badge counts are accurate and
  // pagination can be done client-side without extra API calls.
  useEffect(() => {
    (async () => {
      if (context?.contracts?.activeContracts?.length > 0) {
        setLoading(true)
        const contractsList: string[] = context.contracts.activeContracts.map(
          (c: any) => c.contractNumber
        )

        const forceBtpRequest = true
        location.state = null

        await repairAndInquiriesApi.getByContractsList(JSON.stringify(contractsList), 0, forceBtpRequest, 0, 999)
          .then(res => {
            const reports = res
              .filter((r: ServiceRequestItem) => r.contract_number !== null)
              .sort((a, b) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime())
            setAllRequests(reports)
            setVisibleCount(PAGE_SIZE)
            setShowErrorAlert(false)
          })
          .catch(err => {
            setShowErrorAlert(true)
            setErrorAlertMsg(generateError(err?.response?.data?.message))
          })
      }
      setLoading(false)
    })()
  }, [context?.contracts?.activeContracts, refreshKey])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [activeStatus])

  const handleLoadMore = () => {
    if (loadingMore) return
    setLoadingMore(true)
    setTimeout(() => {
      setVisibleCount((c) => c + PAGE_SIZE)
      setLoadingMore(false)
    }, 200)
  }

  return (
    <div>
      <PageHeader
        Icon={WrenchSvgIcon}
        title={t('NAVBAR.REPAIRS_AND_INQUIRIES')}
        subtitle={t('REPAIR_AND_INQUIRIES.SUBHEAD')}
        breadcrumbs={[
          { label: t('NAVBAR.TENANT_SERVICES') },
          { label: t('NAVBAR.REPAIRS_AND_INQUIRIES') }
        ]}
        actions={[
          { label: t('REPAIR_AND_INQUIRIES.CREATE_REQUEST'), onClick: () => setShowWizard(true), Icon: PlusIcon }
        ]}
      />
      {loading && <LoadingOverlay />}
      {!loading && (allRequests?.length > 0 || (allRequests?.length === 0 && activeStatus !== 0)) && (
        <Multipanel
          totalRequests={allRequests}
          visibleCount={visibleCount}
          activeStatus={activeStatus}
          setActiveStatus={setActiveStatus}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
        />
      )}
      {!loading && activeStatus === 0 && allRequests?.length === 0 && (
        <NoRequests />
      )}
      <NewDamageReportWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSubmitted={() => {
          setVisibleCount(PAGE_SIZE)
          setRefreshKey((k) => k + 1)
        }}
      />
      <Snackbar open={showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <Alert severity='error' sx={{ width: '100%' }} variant='filled' role='alert' aria-live='assertive'>
          {errorAlertMsg}
        </Alert>
      </Snackbar>
    </div>
  )
}
